import { Controller } from "@hotwired/stimulus"
import { showError } from "../lib/notifications"

const STRIPE_JS_SRC = "https://js.stripe.com/v3/"
const STRIPE_BUY_BUTTON_SRC = "https://js.stripe.com/v3/buy-button.js"

const scriptPromises = new Map()

function loadExternalScript(src) {
  const existingPromise = scriptPromises.get(src)
  if (existingPromise) return existingPromise

  const promise = new Promise((resolve, reject) => {
    const existingScript = document.querySelector(`script[src="${src}"]`)
    if (existingScript) {
      existingScript.addEventListener("load", resolve, { once: true })
      existingScript.addEventListener("error", () => reject(new Error(`Failed to load ${src}`)), { once: true })
      return
    }

    const script = document.createElement("script")
    script.src = src
    script.async = true
    script.onload = resolve
    script.onerror = () => reject(new Error(`Failed to load ${src}`))
    document.head.appendChild(script)
  })

  scriptPromises.set(src, promise)
  return promise
}

async function ensureScriptLoaded(src, isReady) {
  if (isReady()) return

  await loadExternalScript(src)

  if (!isReady()) {
    throw new Error(`Loaded ${src}, but expected globals were not initialized`)
  }
}

export default class extends Controller {
  static identifier = "shop"
  static targets = ["productGrid"]
  static values = { stripePublicKey: String }

  connect() {
    this.stripe = null
    this.stripePromise = null
    this.checkout = null
    this.loadStripeBuyButtonIfPresent()
    this.setupEventListeners()
    this.checkPaymentCompletion()
  }

  disconnect() {
    this.cleanup()
  }

  stripePublicKey() {
    const stripeKeyFromData =
      (this.hasStripePublicKeyValue && this.stripePublicKeyValue) || null

    return stripeKeyFromData || document.querySelector('meta[name="stripe-public-key"]')?.content || null
  }

  loadStripeBuyButtonIfPresent() {
    const buyButton = this.element.querySelector("stripe-buy-button")
    if (!buyButton) return

    ensureScriptLoaded(STRIPE_BUY_BUTTON_SRC, () => {
      if (typeof customElements === "undefined") return false
      return !!customElements.get("stripe-buy-button")
    }).catch((error) => {
      console.error("Stripe buy button failed to load:", error)
    })
  }

  async ensureStripeInitialized() {
    if (this.stripe) return this.stripe

    if (!this.stripePromise) {
      this.stripePromise = this.initStripe()
    }

    this.stripe = await this.stripePromise
    if (!this.stripe) throw new Error("Stripe failed to initialize.")
    return this.stripe
  }

  async initStripe() {
    const stripeKey = this.stripePublicKey()
    if (!stripeKey) throw new Error("Missing Stripe public key.")

    await ensureScriptLoaded(STRIPE_JS_SRC, () => typeof window.Stripe === "function")
    return window.Stripe(stripeKey)
  }

  setupEventListeners() {
    this.element.addEventListener('click', this.handlePurchaseClick.bind(this))
    document.addEventListener('turbo:before-visit', this.cleanup.bind(this))
  }

  cleanup() {
    if (this.checkout) {
      try {
        this.checkout.destroy()
      } catch (error) {
        console.log('Checkout cleanup (expected):', error)
      }
      this.checkout = null
    }
  }

  async handlePurchaseClick(e) {
    if (!e.target.classList.contains('purchase-btn')) return

    const { product, productName, price, recipientId, recipientName } = e.target.dataset
    const button = e.target

    this.setLoadingState(button, true)

    try {
      await this.ensureStripeInitialized()
      this.cleanup()
      const clientSecret = await this.createCheckoutSession(product, recipientId)
      this.showCheckoutInterface(productName, price, recipientName)
      await this.mountCheckout(clientSecret)
    } catch (error) {
      this.handleError(error, button)
    }
  }

  async createCheckoutSession(product, recipientId) {
    const response = await fetch('/purchases', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
      },
      body: JSON.stringify({ product, recipient_id: recipientId })
    })

    const { client_secret, error } = await response.json()
    if (error) throw new Error(error)
    return client_secret
  }

  showCheckoutInterface(productName, price, recipientName) {
    if (!this.hasProductGridTarget) return

    this.productGridTarget.innerHTML = `
      <section class="card border border-base-200 bg-base-100 shadow-lg">
        <div class="space-y-6">
          <header class="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div class="space-y-2">
              <span class="badge badge-outline badge-sm uppercase tracking-[0.25em]">Checkout</span>
              <h2 class="card-title text-2xl">Complete Your Purchase</h2>
            </div>
            <div class="rounded-box border border-base-200 bg-base-200/40 p-4 text-sm text-base-content/80" data-checkout-summary>
              <p class="font-medium">${productName}</p>
              <p class="text-lg font-semibold text-primary">${price}</p>
            </div>
            <button type="button" class="btn btn-ghost btn-sm gap-2 self-start back-to-shop-btn" data-action="click->shop#backToShop">
              ← Back to Shop
            </button>
          </header>
          <div class="rounded-box border border-dashed border-base-300 bg-base-200/40 p-4">
            <div id="checkout-form" class="stripe-checkout-form min-h-[360px]"></div>
          </div>
        </div>
      </section>
    `

    if (recipientName) {
      const summary = this.productGridTarget.querySelector("[data-checkout-summary]")
      if (!summary) return

      const line = document.createElement("p")
      line.className = "text-xs text-base-content/70"
      line.appendChild(document.createTextNode("Recipient: "))

      const nameSpan = document.createElement("span")
      nameSpan.className = "font-medium"
      nameSpan.textContent = recipientName
      line.appendChild(nameSpan)

      summary.appendChild(line)
    }
  }

  backToShop() {
    window.location.href = window.location.pathname + window.location.search
  }

  async mountCheckout(clientSecret) {
    const stripe = await this.ensureStripeInitialized()
    this.checkout = await stripe.initEmbeddedCheckout({ clientSecret })
    this.checkout.mount('#checkout-form')
  }

  setLoadingState(button, loading) {
    button.disabled = loading
    if (loading) {
      if (!button.dataset.originalText) button.dataset.originalText = button.textContent
      button.textContent = 'Loading...'
    } else {
      button.textContent = button.dataset.originalText || button.textContent
    }
  }

  handleError(error, button) {
    console.error('Payment error:', error)
    showError('Payment initialization failed. Please try again.')
    this.setLoadingState(button, false)
  }

  async checkPaymentCompletion() {
    const urlParams = new URLSearchParams(window.location.search)
    const sessionId = urlParams.get('session_id')

    if (!sessionId) return

    try {
      const response = await fetch(`/purchases/session_status?session_id=${sessionId}`)
      const { status } = await response.json()

      if (status === 'complete') {
        window.location.href = '/shop'
      }
    } catch (error) {
      console.error('Error checking session status:', error)
    }
  }
}
